import { capitalize } from "common/utils/capitalize";
import cx from "ui/cx";
import styles from "./ReadOnlyTreeSelect.module.scss";
import type { TreeSelectNodeType } from "./treeSelectTypes";

interface ReadOnlyTreeSelectProps {
  tree: TreeSelectNodeType[];
  selectedIds: string[];
  /**
   * Optional callback to transform display labels for specific node types.
   * Useful for adding contextual information like parent names.
   */
  getLabelContext?: (node: TreeSelectNodeType, parentName?: string) => { displayLabel: string; nameToPass?: string };
  /**
   * Optional: display as a flat list showing only selected nodes with parent context
   */
  flatDisplay?: boolean;
  /**
   * Optional: determines whether the current node should establish itself as the parent context.
   * Defaults to treating nodes with descriptor === "community" as parent-establishing.
   */
  shouldEstablishParent?: (node: TreeSelectNodeType) => boolean;
  /**
   * Optional: function to format the descriptor label. If not provided, defaults to capitalized descriptor.
   */
  getDescriptorLabel?: (descriptor: string | undefined) => string;
  /**
   * Optional: additional className to apply to the root tree element
   */
  className?: string;
}

/**
 * ReadOnlyTreeSelect - A read-only companion to TreeSelect for displaying selected tree nodes.
 * Only shows nodes that are selected or have selected descendants.
 */
export default function ReadOnlyTreeSelect({
  tree,
  selectedIds,
  getLabelContext,
  flatDisplay = false,
  shouldEstablishParent = (node: TreeSelectNodeType) => node.descriptor?.toLowerCase() === "community",
  getDescriptorLabel,
  className,
}: ReadOnlyTreeSelectProps) {
  const formatDescriptor = (descriptor: string | undefined): string => {
    if (!descriptor) {
      return "";
    }
    return getDescriptorLabel ? getDescriptorLabel(descriptor) : capitalize(descriptor);
  };
  if (flatDisplay) {
    const flatNodesWithParent: Array<{ node: TreeSelectNodeType; parentName?: string; parentId?: string }> = [];
    const collectNodes = (nodes: TreeSelectNodeType[], parentName?: string, parentId?: string) => {
      nodes.forEach((node) => {
        const { nameToPass = parentName } = getLabelContext?.(node, parentName) ?? { nameToPass: parentName };
        const idToPass = shouldEstablishParent(node) ? node.id : parentId;
        if (selectedIds.includes(node.id)) {
          flatNodesWithParent.push({ node, parentName, parentId });
        }
        if (node.children.length > 0) {
          collectNodes(node.children, nameToPass, idToPass);
        }
      });
    };
    collectNodes(tree);

    return (
      <div className={cx(styles.tree, className)}>
        {flatNodesWithParent.map(({ node, parentName, parentId }) => {
          const { displayLabel } = getLabelContext?.(node, parentName) ?? { displayLabel: node.label };
          const isChild = parentId && selectedIds.includes(parentId);
          return (
            <div key={node.id} className={`${styles.row} ${isChild ? styles.indented : ""}`}>
              <span className={styles.bullet}>•</span>
              <span className={styles.label}>{displayLabel}</span>
              {node.descriptor && <span className={styles.descriptor}>{formatDescriptor(node.descriptor)}</span>}
            </div>
          );
        })}
      </div>
    );
  }
  const renderNode = (node: TreeSelectNodeType, parentName?: string): React.ReactNode | null => {
    const visibleChildren = node.children.filter((child) => hasSelectedRecursive(child, selectedIds));
    const isSelected = selectedIds.includes(node.id);
    const hasVisibleChildren = visibleChildren.length > 0;
    const shouldShow = isSelected || hasVisibleChildren;

    if (!shouldShow) {
      return null;
    }

    // Node is shown only as a structural ancestor (to provide hierarchy context),
    // not because it was directly selected
    const isAncestorOnly = !isSelected && hasVisibleChildren;

    // Use custom label transformer if provided, otherwise use default
    const { displayLabel, nameToPass } = getLabelContext?.(node, parentName) ?? {
      displayLabel: node.label,
      nameToPass: parentName,
    };

    return (
      <div key={node.id} className={styles.treeContainer}>
        <div className={cx(styles.row, isAncestorOnly && styles.ancestor)}>
          <span className={styles.bullet}>•</span>
          <span className={styles.label}>{displayLabel}</span>
          {node.descriptor && <span className={styles.descriptor}>{formatDescriptor(node.descriptor)}</span>}
        </div>
        {visibleChildren.length > 0 && (
          <div className={styles.children}>{visibleChildren.map((child) => renderNode(child, nameToPass))}</div>
        )}
      </div>
    );
  };

  return <div className={cx(styles.tree, className)}>{tree.map((node) => renderNode(node))}</div>;
}

function hasSelectedRecursive(node: TreeSelectNodeType, selectedIds: string[]): boolean {
  if (selectedIds.includes(node.id)) {
    return true;
  }
  return node.children.some((child) => hasSelectedRecursive(child, selectedIds));
}
